import { useEffect, useState } from 'react';
import { apiPost, apiDelete } from '../api.js';
import { useGlobalStore } from '@digiwedge/global-state';

type OverrideRow = { host: string; category: string; reason?: string; updatedAt?: string };

export function OverridesManager({
  registryUrl,
  siteKey,
  onClose,
  initialQuery,
}: {
  registryUrl: string;
  siteKey?: string;
  onClose(): void;
  initialQuery?: string;
}) {
  const [query, setQuery] = useState(initialQuery || '');
  const [rows, setRows] = useState<OverrideRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState<'all' | 'global' | 'site'>(siteKey ? 'site' : 'all');
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  const load = async (q = query, ofs = offset) => {
    setLoading(true);
    try {
      const body: {
        query: string;
        limit: number;
        offset: number;
        scope: 'all' | 'global' | 'site';
        site_key?: string;
      } = { query: q, limit, offset: ofs, scope };
      if (scope === 'site' && siteKey) body.site_key = siteKey;
      const res = await apiPost<{ items: OverrideRow[]; total: number }>(
        `${registryUrl}/admin/classifier/overrides:list`,
        body,
      );
      setRows(res.items);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(query, 0).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registryUrl, scope, siteKey, limit]);

  const onSearch = async () => {
    setOffset(0);
    load(query, 0);
  };
  const remove = async (host: string) => {
    if (!confirm(`Remove override for ${host}?`)) return;
    const url = new URL(`${registryUrl}/admin/classifier/override`);
    url.searchParams.set('host', host);
    if (scope === 'site' && siteKey) url.searchParams.set('site_key', siteKey);
    await apiDelete(url.toString());
    await load(query, offset);
  };
  const next = async () => {
    const ofs = offset + limit;
    setOffset(ofs);
    await load(query, ofs);
  };
  const prev = async () => {
    const ofs = Math.max(0, offset - limit);
    setOffset(ofs);
    await load(query, ofs);
  };

  const exportCsv = async () => {
    const token = useGlobalStore.getState().token ?? undefined;
    const body: Record<string, unknown> = { query, scope };
    if (scope === 'site' && siteKey) body.site_key = siteKey;
    const r = await fetch(`${registryUrl}/admin/classifier/overrides:export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'overrides.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTemplate = () => {
    const csv = 'host,category,site_key,reason\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'overrides.template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const importCsv = async (file: File) => {
    const text = await file.text();
    const token = useGlobalStore.getState().token ?? undefined;
    const body: Record<string, unknown> = { csv: text };
    if (scope === 'site' && siteKey) body.default_site_key = siteKey;
    const r = await fetch(`${registryUrl}/admin/classifier/overrides:import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
    const res = await r.json();
    alert(
      `Import: created=${res.created || 0}, updated=${res.updated || 0}, errors=${res.errors || 0}`,
    );
    await load(query, offset);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 'min(560px, 90vw)',
        background: '#fff',
        boxShadow: '-12px 0 24px rgba(0,0,0,.1)',
        padding: 16,
        zIndex: 2147483647,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h3 style={{ margin: 0 }}>Overrides</h3>
        <div style={{ marginLeft: 'auto' }}>
          <button onClick={onClose}>Close</button>
        </div>
      </div>

      <div
        style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <input
          placeholder="search host…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: 1, minWidth: 240 }}
        />
        <button onClick={onSearch} disabled={loading}>
          {loading ? 'Searching…' : 'Search'}
        </button>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          Scope:
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value as 'all' | 'global' | 'site')}
          >
            <option value="all">All</option>
            <option value="global">Global</option>
            <option value="site" disabled={!siteKey}>
              This site
            </option>
          </select>
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          Page size:
          <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))}>
            <option>25</option>
            <option>50</option>
            <option>100</option>
          </select>
        </label>
      </div>

      <p style={{ color: '#666' }}>
        {total} override(s) — showing {rows.length} (offset {offset})
      </p>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th align="left">Host</th>
            <th>Category</th>
            <th align="left">Reason</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.host} style={{ borderTop: '1px solid #eee' }}>
              <td>
                <code>{r.host}</code>
              </td>
              <td align="center">{r.category}</td>
              <td>{r.reason ?? '—'}</td>
              <td align="right">
                <button onClick={() => remove(r.host)}>Remove</button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={4}>No overrides</td>
            </tr>
          )}
        </tbody>
      </table>
      <div
        style={{
          marginTop: 12,
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={exportCsv}>Export CSV</button>
          <button onClick={downloadTemplate}>Download template</button>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            Import CSV:
            <input
              type="file"
              accept=".csv"
              onChange={(e) => e.target.files?.[0] && importCsv(e.target.files[0])}
            />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={prev} disabled={offset === 0 || loading}>
            Prev
          </button>
          <button onClick={next} disabled={offset + limit >= total || loading}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
